/**
 * Data hooks — the only thing screens import to read domain data. All hooks read
 * one org-scoped dataset query and select their slice, so switching org swaps
 * every screen's data at once and demo mutations/reset (which target the same
 * key) reflect live.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type {
  AppDef,
  Agent,
  Catalog,
  Conversation,
  Department,
  Objective,
  Person,
  ProcessRow,
} from '@wand/shared';
import { useCatalog } from '../api/hooks.js';
import type { InboxItem } from '../data/requests.js';
import type { Group } from '../data/groups.js';
import type { SignalMetric, SignalThreshold } from '../data/metrics.js';
import type { MyWorkData } from '../data/my_work.js';
import type { HomeContent } from '../data/home.js';
import type { MonitorContent } from '../data/monitor.js';
import type { UniversityContent } from '../data/university.js';
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import { EMPTY_AGENT_DETAIL } from '@wand/shared/dataset/agent_detail.js';
import type { KnowledgeData } from '@wand/shared/dataset/knowledge.js';
import type { CanonData } from '@wand/shared/dataset/canon.js';
import { deriveAgentDetail } from '../viewmodels/agent_detail_derive.js';
import { useAgentDetailScope } from '../agentdetail/scope.js';
import type {
  CeoAuditEvent,
  CeoBoard,
  CeoData,
  CeoEscalation,
  CeoFramework,
  CeoInsight,
  CeoObjective,
  CeoOrgUnit,
  CeoPolicy,
  CeoRule,
  CeoViolation,
  CeoWorker,
} from '@wand/shared/dataset/ceo.js';
import type { LiveCase } from '../data/cases.js';
import type { ProcessDetail } from '../data/process_detail.js';
import { resolveProcessDetail } from '../viewmodels/process_detail.js';
import { orgKey } from '../api/keys.js';
import { useOrgId } from '../org/OrgContext.js';
import { useDemoApi, useGateway } from './GatewayContext.js';
import type { OrgDataset } from './dataset.js';

/** Base dataset query; every screen hook selects a slice of it. */
function useDataset<T>(select: (d: OrgDataset) => T): UseQueryResult<T> {
  const org = useOrgId();
  const gw = useGateway();
  return useQuery({
    queryKey: orgKey.dataset(org),
    queryFn: () => gw.getDataset(org),
    select,
  });
}

export function useAgents(): UseQueryResult<Agent[]> {
  return useDataset((d) => d.agents);
}

export function useAgent(pk: string | undefined): UseQueryResult<Agent | undefined> {
  return useDataset((d) => (pk ? d.agents.find((a) => a.pk === pk) : undefined));
}

export function useInbox(): UseQueryResult<InboxItem[]> {
  return useDataset((d) => d.inbox);
}

export function useProcesses(): UseQueryResult<ProcessRow[]> {
  return useDataset((d) => d.processes);
}

/** Per-process detail: the authored org bundle if present, else the template. */
export function useProcessDetail(pk: string): UseQueryResult<ProcessDetail> {
  return useDataset(
    (d) =>
      d.processDetailByPk?.[pk] ??
      resolveProcessDetail(
        pk,
        d.processes.find((p) => p.pk === pk),
      ),
  );
}

export function useGroups(): UseQueryResult<Group[]> {
  return useDataset((d) => d.groups);
}

/** The org people directory — the source for every people picker/list. */
export function usePeople(): UseQueryResult<Person[]> {
  return useDataset((d) => d.people ?? []);
}

/** The org departments / divisions. */
export function useDepartments(): UseQueryResult<Department[]> {
  return useDataset((d) => d.departments ?? []);
}

/** The org objectives (OKRs) — feeds process & agent objective pickers. */
export function useObjectives(): UseQueryResult<Objective[]> {
  return useDataset((d) => d.objectives ?? []);
}

/** The org's connected applications — feeds the canvas app/connector picker. */
export function useApps(): UseQueryResult<AppDef[]> {
  return useDataset((d) => d.apps ?? []);
}

/**
 * The catalog the canvas consumes, with org-scoped data overlaid on the global
 * BFF catalog: `agents`, `objectives`, `apps`, `metrics` and `thresholds` follow
 * the active org (falling back to the global catalog when the org hasn't scoped
 * its own — only `apps` still carries a global connector catalog). This is the
 * one place the canvas becomes org-aware, so the engine, property panel,
 * settings and app picker all agree without threading org data through them.
 */
export function useCanvasCatalog(): { data?: Catalog; isError: boolean; error: Error | null } {
  const cat = useCatalog();
  const agents = useAgents().data ?? [];
  const objectives = useObjectives().data ?? [];
  const apps = useApps().data ?? [];
  const signalMetrics = useSignalMetrics().data ?? [];
  const thresholds = useThresholds().data ?? [];
  const data = useMemo<Catalog | undefined>(() => {
    if (!cat.data) return undefined;
    return {
      ...cat.data,
      agents: agents.length ? agents : cat.data.agents,
      objectives: objectives.length ? objectives : cat.data.objectives,
      apps: apps.length ? apps : cat.data.apps,
      // The org's rich Signals records are the source of truth; the canvas
      // pickers consume the light catalog shapes derived from them.
      metrics: signalMetrics.length
        ? signalMetrics.map((m) => ({ id: m.id, n: m.name, unit: m.unit ?? '', src: m.srcLabel }))
        : cat.data.metrics,
      thresholds: thresholds.length
        ? thresholds.map((t) => ({
            id: t.id,
            n: t.name,
            metric: t.metric,
            op: t.dir,
            value: Number.parseFloat(t.limit) || 0,
            unit: t.limit.replace(/^[\s\d.,+-]+/, ''),
          }))
        : cat.data.thresholds,
    };
  }, [cat.data, agents, objectives, apps, signalMetrics, thresholds]);
  return { data, isError: cat.isError, error: cat.error };
}

export function useSignalMetrics(): UseQueryResult<SignalMetric[]> {
  return useDataset((d) => d.signalMetrics);
}

export function useThresholds(): UseQueryResult<SignalThreshold[]> {
  return useDataset((d) => d.thresholds);
}

export function useMyWork(): UseQueryResult<MyWorkData> {
  return useDataset((d) => d.mywork);
}

export function useConversations(): UseQueryResult<Conversation[]> {
  return useDataset((d) => d.conversations);
}

export function useHomeContent(): UseQueryResult<HomeContent> {
  return useDataset((d) => d.home);
}

export function useMonitor(): UseQueryResult<MonitorContent> {
  return useDataset((d) => d.monitor);
}

export function useUniversity(): UseQueryResult<UniversityContent> {
  return useDataset((d) => d.university);
}

/* ---- the dated record (optional, org-scoped) ---- */

/** The point-in-time corpus, wiki and dependency-edge register. Absent for orgs
 *  that ship no `knowledge.json`, which is how the Record screen stays scoped. */
export function useKnowledge(): UseQueryResult<KnowledgeData | undefined> {
  return useDataset((d) => d.knowledge);
}

/* ---- the governed knowledge layer (optional, org-scoped) ---- */

/** Policy-scoped knowledge bases with bi-temporal document and graph revisions.
 *  Absent for orgs that ship no `canon.json`, which is how the Knowledge screen
 *  stays scoped — the same pattern as `useKnowledge` above, and deliberately NOT the
 *  same collection: that one is the single dated record behind `/record`. */
export function useCanon(): UseQueryResult<CanonData | undefined> {
  return useDataset((d) => d.canon);
}

/* ---- CEO governance board (optional, org-scoped) ---- */

export function useCeo(): UseQueryResult<CeoData | undefined> {
  return useDataset((d) => d.ceo);
}
export function useCeoBoard(): UseQueryResult<CeoBoard | undefined> {
  return useDataset((d) => d.ceo?.board);
}
export function useCeoOrgUnits(): UseQueryResult<CeoOrgUnit[]> {
  return useDataset((d) => d.ceo?.orgUnits ?? []);
}
export function useCeoWorkforce(): UseQueryResult<CeoWorker[]> {
  return useDataset((d) => d.ceo?.workforce ?? []);
}
export function useCeoObjectives(): UseQueryResult<CeoObjective[]> {
  return useDataset((d) => d.ceo?.okrs ?? []);
}
export function useCeoPolicies(): UseQueryResult<CeoPolicy[]> {
  return useDataset((d) => d.ceo?.policies ?? []);
}
export function useCeoFrameworks(): UseQueryResult<CeoFramework[]> {
  return useDataset((d) => d.ceo?.frameworks ?? []);
}
export function useCeoRules(): UseQueryResult<CeoRule[]> {
  return useDataset((d) => d.ceo?.rules ?? []);
}
export function useCeoEscalations(): UseQueryResult<CeoEscalation[]> {
  return useDataset((d) => d.ceo?.escalations ?? []);
}
export function useCeoAudit(): UseQueryResult<{ violations: CeoViolation[]; log: CeoAuditEvent[] }> {
  return useDataset((d) => d.ceo?.audit ?? { violations: [], log: [] });
}
export function useCeoInsights(): UseQueryResult<CeoInsight[]> {
  return useDataset((d) => d.ceo?.insights ?? []);
}

/**
 * Agent Detail content, derived per-agent from the base bundle when a detail
 * scope is active (see `AgentDetailScopeProvider`). Off the detail screen there's
 * no scope and the base bundle is returned unchanged.
 */
/**
 * Fill any collection a bundle does not carry from `EMPTY_AGENT_DETAIL`.
 *
 * Every one of the ten Agent Detail tabs reads its slice through a non-null
 * assertion — `useAgentDetail().data!.tasks.schedules`, `…!.access.people`, and
 * twelve more. A bundle missing one key therefore does not degrade: it throws
 * inside a React render and white-screens the whole route.
 *
 * That is not hypothetical. `DemoGateway.ensure()` shallow-merges persisted state
 * over the baseline (`{ ...seed, ...persisted }`), so a browser holding an
 * `agentDetailByPk` saved by an EARLIER build replaces the current one wholesale —
 * and any key that older copy lacked takes a tab down. "Reset demo" fixed it by
 * deleting the state; this fixes it by making a partial bundle harmless.
 *
 * Two levels is exactly right: the tabs destructure one level below each
 * collection (`{ people, usage } = …access`), so the sub-objects have to be whole
 * too, but nothing reads deeper than that. Authored values always win — this only
 * ever ADDS the empty defaults the shared constant already defines for empty-org
 * safety. `check:demo:nda` separately asserts no authored bundle relies on it, so
 * a genuine omission still fails the build rather than silently rendering blank.
 */
function withDefaults(b: AgentDetailContent): AgentDetailContent {
  const out = { ...EMPTY_AGENT_DETAIL, ...b } as Record<string, unknown>;
  for (const [key, empty] of Object.entries(EMPTY_AGENT_DETAIL)) {
    if (empty && typeof empty === 'object' && !Array.isArray(empty)) {
      const got = out[key];
      out[key] = got && typeof got === 'object' && !Array.isArray(got) ? { ...empty, ...got } : empty;
    }
  }
  return out as unknown as AgentDetailContent;
}

export function useAgentDetail(): UseQueryResult<AgentDetailContent> {
  const scope = useAgentDetailScope();
  return useDataset((d) => {
    // Prefer an authored per-agent bundle; otherwise derive one from the base.
    const authored = scope?.pk ? d.agentDetailByPk?.[scope.pk] : undefined;
    return withDefaults(authored ?? deriveAgentDetail(d.agentDetail, scope));
  });
}

export function useCases(): UseQueryResult<LiveCase[]> {
  return useDataset((d) => d.cases);
}

/** Create an agent, persist it, then refresh the org dataset so it appears live. */
export function useCreateAgent() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Agent, 'pk'>) => gw.createAgent(org, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/** Create a group, persist it, then refresh the org dataset so it appears live. */
export function useCreateGroup() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<Group, 'pk'>) => gw.createGroup(org, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/** Create a Signals metric, persist it, then refresh the org dataset. */
export function useCreateMetric() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<SignalMetric, 'id'> & { id?: string }) => gw.createMetric(org, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/** Execute a resolving inbox action (server applies its declared resolution), then refresh. */
export function useResolveInboxItem() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, actionId }: { id: string; actionId: string }) =>
      gw.resolveInboxItem(org, id, actionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/** Reply to an inbox item (resolves it as "Replied"), then refresh. */
export function useReplyInboxItem() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => gw.replyInboxItem(org, id, text),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/** Ask the emitting agent a question (item stays pending), then refresh. */
export function useAskInboxItem() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => gw.askInboxItem(org, id, text),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/** Update a group (add/remove members, edit fields), persist, then refresh. */
export function useUpdateGroup() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pk, patch }: { pk: string; patch: Partial<Omit<Group, 'pk'>> }) =>
      gw.updateGroup(org, pk, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.dataset(org) }),
  });
}

/**
 * Demo reset controls, or null when demo mode is off. Each reset clears the
 * stored state and invalidates the affected org's queries so the UI re-reads the
 * freshly re-seeded data.
 */
export function useResetDemo(): {
  resetCurrent: () => Promise<void>;
  resetAll: () => Promise<void>;
} | null {
  const demo = useDemoApi();
  const org = useOrgId();
  const qc = useQueryClient();

  const resetCurrent = useCallback(async () => {
    if (!demo) return;
    await demo.reset(org);
    await qc.invalidateQueries({ queryKey: orgKey.all(org) });
  }, [demo, org, qc]);

  const resetAll = useCallback(async () => {
    if (!demo) return;
    await demo.resetAll();
    await qc.invalidateQueries({ queryKey: ['org'] });
  }, [demo, qc]);

  return demo ? { resetCurrent, resetAll } : null;
}
